import { useMediaQuery } from "react-responsive";

import { Button, Card, Heading, Link, List, Span } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { useThumbtackModalContext } from "components/thumbtack-modal";
import { screenSizes } from "consts";
import { type KeystoneReferral } from "data/keystone-api-mapping";
import { usePolicyDetail } from "data/ss-data-hooks";
import { capitalize, checkForNaStringValue } from "utils";

import styles from "./write-out-card.module.scss";

export const WriteOutCard = ({
  referral,
  showGallery,
}: {
  referral: KeystoneReferral;
  showGallery: () => void;
}) => {
  const { data } = usePolicyDetail();
  const { zip: zipCode } = data?.userDetails?.propertyAddress ?? {};
  const { openThumbtackModal } = useThumbtackModalContext();
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });
  const maxPhotosInRow = isMobile ? 3 : 5;
  const {
    actionStatement,
    category,
    groupedIssueStatements,
    groupedReasonings,
    inspectionReviewAssetIds,
    issueStatement,
    locationDetail,
    reasoning,
    searchTerm,
  } = referral;

  let writeOutHeading: string | null =
    issueStatement?.replace(/^.*? - /, "") ?? "";
  if (groupedIssueStatements?.length) {
    writeOutHeading = "Multiple issues";
  }
  writeOutHeading = checkForNaStringValue(writeOutHeading);
  const reasoningDisplay = checkForNaStringValue(reasoning ?? "");
  let reasonings: string[] = [];
  if (groupedReasonings?.length) {
    reasonings = groupedReasonings;
  } else if (reasoningDisplay) {
    reasonings = [reasoningDisplay];
  }
  const locationDisplay = checkForNaStringValue(locationDetail ?? "");
  const categoryDisplay = checkForNaStringValue(category ?? "");
  const actionStatementDisplay = checkForNaStringValue(actionStatement ?? "");
  const validSearchTerm = checkForNaStringValue(searchTerm ?? "");
  const subHeading: string = categoryDisplay
    ? `${categoryDisplay} ${locationDisplay ? `• ${locationDisplay}` : ""}`
    : "";

  return (
    <Card>
      <Stack gap="sm">
        {writeOutHeading ? (
          <Heading size="sm">{capitalize(writeOutHeading)}</Heading>
        ) : null}

        <Span color="quiet" size="sm">
          {subHeading}
        </Span>

        {actionStatementDisplay ? (
          <div className={styles.resolveBox}>
            <Span bold>How to resolve:</Span>
            <List type="ul">
              <li className={styles.listItem}>{actionStatementDisplay}</li>
            </List>

            {validSearchTerm ? (
              <Span>
                Need help?{" "}
                <Button
                  appearance="link"
                  onPress={() =>
                    openThumbtackModal(zipCode ?? "", validSearchTerm)
                  }
                  data-testid={`write-out-contractor-button: "${validSearchTerm}"`}
                >
                  Find a qualified contractor.
                </Button>
              </Span>
            ) : null}
          </div>
        ) : null}

        {reasonings.length ? (
          <div className={styles.reasoning}>
            <Span bold>Why is this an issue?</Span>
            {reasonings.map((reasoningString, idx) => (
              <Span key={idx}>{reasoningString}</Span>
            ))}
          </div>
        ) : null}

        {inspectionReviewAssetIds?.length ? (
          <>
            <Span bold>Inspection photos</Span>
            <ul className={styles.fileList}>
              {(inspectionReviewAssetIds.length > maxPhotosInRow
                ? inspectionReviewAssetIds.slice(0, maxPhotosInRow - 1)
                : inspectionReviewAssetIds
              ).map((assetId) => (
                <li className={styles.fileListItem} key={assetId}>
                  <img
                    className={styles.fileTile}
                    src={`/api/inspection/image/${assetId}`}
                    alt={`Inspection asset: ${assetId}`}
                    height={94}
                    loading="lazy"
                  />
                </li>
              ))}
              {inspectionReviewAssetIds.length > maxPhotosInRow ? (
                <li className={styles.fileTileOverflow}>
                  <Link
                    appearance="link"
                    onPress={() => showGallery()}
                    className={styles.morePhotosLink}
                  >
                    +{inspectionReviewAssetIds.length - (maxPhotosInRow - 1)}{" "}
                    more
                  </Link>
                </li>
              ) : null}
            </ul>
          </>
        ) : null}
      </Stack>
    </Card>
  );
};
